<template>
  <div>
    <h2>
      <span class="step-name">Lamb Production</span>
    </h2>
    <div class="step-section">
      <radio-input
        label="Intensive Lamb Rearing"
        field-name="sheep_is_rearing_lambs"
        :choices="[
          { value: 'Yes', display: 'Yes' },
          { value: 'No', display: 'No' },
        ]"
      >
      </radio-input>

      <discussion-points
        title="Intensive lamb rearing discussion points"
        comments-field="sheep_is_rearing_lambs_discussion"
        points="Housing, milk powder types, robotic feeding, pasture management, coccidia"
      >
      </discussion-points>
      <discussion-points
        title="Lamb Growth Discussion Points"
        comments-field="lamb_growth_discussion_points"
        points="Weaning weight, weaning kill, average carcass weight, killing pattern, lamb survival."
      >
      </discussion-points>
    </div>

    <div class="step-section">
      <h3>Parasite Management</h3>

      <h3>All Drench Product Purchases</h3>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Purchase Date</th>
            <th>Animal Name</th>
            <th>Stock Code - Name</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in getChartData('sheep_drench_purchases', 'SheepReproductionPlan')" :key="index">
            <td class="text-left">{{ row['Purchase Date'] }}</td>
            <td class="text-left">{{ row['Animal Name'] }}</td>
            <td class="text-left">{{ row['Stock Code - Name'] }}</td>
            <td class="text-left">{{ row['Quantity'] }}</td>
          </tr>
        </tbody>
      </table>

      <text-input
        label="Other drench products/actives used"
        field-name="sheep_other_drench_products_actives_used_not_vs"
        model-name="SheepReproductionPlan"
      >
      </text-input>

      <discussion-points
        title="Lamb Drench Discussion"
        comments-field="lamb_drench_discussion"
        points="Lamb drenching policy, pretup/prelamb, alternative management techniques"
      >
      </discussion-points>
    </div>

    <div class="step-section">
      <text-input label="Most recent charge for FECRT" field-name="sheep_fec_recent_fecrt" disabled> </text-input>
      <radio-input
        label="Do you want FECRT testing/drench check? (Include month to make contact)."
        field-name="sheep_fec_fecrt"
        :add-comment="true"
        :add-email-notification="true"
        @on-change="toggleNotifications"
        :choices="[
          { value: 'Yes', display: 'Yes' },
          { value: 'No', display: 'No' },
        ]"
      >
      </radio-input>

      <discussion-points
        title="Lamb Parasite Management Discussion Points"
        comments-field="lamb_parasite_management_discussion_points"
        points="Drench plan, products, drench testing, knock out, quarantine, refugia, low risk/high risk pastures"
      >
      </discussion-points>

      <radio-input
        label="Are you interested in attending a wormwise workshop? (Learn more about parasite management and drench resistance)"
        field-name="sheep_wants_wormwise_workshop"
        :choices="[
          { value: 'Yes', display: 'Yes' },
          { value: 'No', display: 'No' },
        ]"
      >
      </radio-input>

      <radio-input
        label="Are you interested in a a specific parasite management consult and plan for your farm? If so, when?"
        field-name="sheep_wants_parasite_mgmt_plan"
        :add-email-notification="true"
        :add-comment="true"
        comment-field-name="lamb_parasite_management_plan"
        @on-change="toggleNotifications"
        :choices="[
          { value: 'Yes', display: 'Yes' },
          { value: 'No', display: 'No' },
        ]"
      >
      </radio-input>
    </div>

    <div class="step-section">
      <discussion-points title="" points="Animal, product, price (if discussed), any other information to help retail">
        <radio-input
          label="Do you want retail to call with drench supply options for your farm?"
          field-name="sheep_wants_drench_offer"
          :add-email-notification="true"
          @on-change="
            updateRequiredFields($event)
            toggleNotifications($event)
          "
          :add-comment="true"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
          ]"
        >
          <text-area
            v-if="getFieldValue('sheep_wants_drench_offer') == 'Yes'"
            label="Additional information for retail - e.g. Month, Product"
            field-name="sheep_wants_drench_offer_notes"
            @on-change="checkAndCompleteStep"
            :required="Boolean(getFieldValue('sheep_wants_drench_offer') == 'Yes')"
          >
          </text-area>
        </radio-input>
      </discussion-points>
    </div>

    <div class="step-section">
      <h3>Lamb Trace Elements</h3>
      <discussion-points
        title="Lamb TE Discussion Points"
        comments-field="lamb_te_discussion_points"
        points="Selenium, Cobalt/B12, Iodine"
      >
      </discussion-points>

      <h3 class="text-left">
        <span class="form-group">
          Trace element test results (LB12)
          <comments field-name="lb12"></comments>
        </span>
      </h3>

      <box-plot-chart :chart-data="get_lb12_data" />
    </div>

    <div class="step-section">
      <h3>Lamb Vaccinations</h3>
      <discussion-points
        title="Lamb Vaccination Discussion"
        comments-field="lamb_vaccination_discussion"
        points="Clostridial (vaccination timing and products), Scabby Mouth (application, storage, human health, check take)"
      >
      </discussion-points>
      <h4>Scabby Mouth Vaccine Purchases</h4>
      <table class="table table-bordered">
        <thead>
          <tr>
            <th>Purchase Date</th>
            <th>Animal Name</th>
            <th>Stock Code - Name</th>
            <th>Quantity</th>
            <th>Doses</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in getChartData('sheep_scabby_vax_purchases')" :key="index">
            <td class="text-left">{{ row['Purchase Date'] }}</td>
            <td class="text-left">{{ row['Animal Name'] }}</td>
            <td class="text-left">{{ row['Stock Code - Name'] }}</td>
            <td class="text-left">{{ row['Quantity'] }}</td>
            <td class="text-left">{{ row['Doses'] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="step-section">
      <h3>Store Lamb Purchases</h3>
      <discussion-points
        title="Store Lamb Discussion"
        comments-field="store_lamb_discussion"
        points="Quarantine drench, parasites, trace elements, vaccinations"
      >
      </discussion-points>
    </div>
  </div>
</template>

<script>
import baseComponent from '@/mixins/base_consult_component_v2'
import BoxPlotChart from '@/components/charts/BoxPlotChart'
import { mapMutations } from 'vuex'

export default {
  name: 'LambProduction',
  mixins: [baseComponent],
  components: {
    BoxPlotChart,
  },
  methods: {
    ...mapMutations('currentConsult', ['addRequiredFields', 'removeRequiredFields']),
    validate: function () {
      if (
        this.getFieldValue('sheep_wants_drench_offer', this.$options.name) == 'Yes' &&
        !this.getFieldValue('sheep_wants_drench_offer_notes', this.$options.name)
      ) {
        return false
      }
      return true
    },
    updateRequiredFields: function ({ fieldName, value }) {
      let requiredField = {
        stepName: this.$options.name,
        value: fieldName,
      }
      if (value == 'Yes') {
        this.addRequiredFields(requiredField)
      } else {
        this.removeRequiredFields(requiredField)
      }
      this.checkAndCompleteStep()
    },
    getLabels(data) {
      return data.map((row) => row['Date'])
    },
    getData(data) {
      return data.map((row) => row['res'].split(', ').map((n) => parseFloat(n)))
    },
    toggleNotifications() {
      this.setEmailNotification({
        slug: 'sheep-fec-fecrt',
        value: this.getFieldValue('sheep_fec_fecrt', this.$options.name) == 'Yes',
      })

      this.setEmailNotification({
        slug: 'sheep-wants-parasite-mgmt-plan',
        value: this.getFieldValue('sheep_wants_parasite_mgmt_plan', this.$options.name) == 'Yes',
      })

      this.setEmailNotification({
        slug: 'do-you-want-some-to-call-with-a-drench-offer',
        value: this.getFieldValue('sheep_wants_drench_offer', this.$options.name) == 'Yes',
      })
    },
  },
  computed: {
    get_lb12_data: function () {
      return {
        labels: this.getLabels(this.getChartData('sheep_lb12_results')),
        datasets: [
          {
            label: 'LB12',
            borderWidth: 2,
            borderColor: 'rgba(0, 0, 0, .4)',
            backgroundColor: 'rgba(51, 122, 183, 0.6)',
            data: this.getData(this.getChartData('sheep_lb12_results')),
          },
        ],
      }
    },
  },
}
</script>
